/**
 * Conversations store.
 *
 * Owns conversation and message state, and is the single write-through path
 * to the storage layer: every mutating action updates in-memory state and
 * persists via `ConversationRepository`, except `updateMessageContent`, which
 * is the high-frequency streaming path and stays in-memory only (see its
 * docstring). Streaming/inference orchestration itself lives in
 * `useChat` (`src/hooks/useChat.ts`), which calls these actions — this store
 * has no knowledge of `chatService` or the AI provider.
 */
import { create } from "zustand";
import type {
  ChatMessage,
  ChatParams,
  Conversation,
  ConversationFolder,
  ConversationSummary,
  KnowledgeScope,
  MessageStatus,
} from "@/types";
import { DEFAULT_KNOWLEDGE_SCOPE } from "@/types";
import { createId, isTauri } from "@/utils";
import { getConversationRepository } from "@/services/storage";
import { toConversationSummary } from "@/services/storage/summarize";
import { conversationOrganizationRepository } from "@/services/storage/conversationOrganizationRepository";
import { logger } from "@/services/logger";
import { useAppStore } from "./appStore";
import { useSettingsStore } from "./settingsStore";

const log = logger.scope("conversations");
const repo = getConversationRepository();

function upsertSummary(
  summaries: ConversationSummary[],
  summary: ConversationSummary,
): ConversationSummary[] {
  const next = summaries.filter((s) => s.id !== summary.id);
  next.push(summary);
  next.sort((a, b) => b.updatedAt - a.updatedAt);
  return next;
}

/** Backfills fields that may be missing from records written before they
 * existed (older `conversations.json` entries, pre-migration). */
function normalize(conversation: Conversation): Conversation {
  return {
    ...conversation,
    knowledgeScope: conversation.knowledgeScope ?? DEFAULT_KNOWLEDGE_SCOPE,
    workspaceId: conversation.workspaceId ?? null,
    pinned: conversation.pinned ?? false,
    folderId: conversation.folderId ?? null,
    parentConversationId: conversation.parentConversationId ?? null,
    branchPointSeq: conversation.branchPointSeq ?? null,
  };
}

function persistConversation(conversation: Conversation): void {
  repo.save(conversation).catch((err: unknown) => {
    log.error("Failed to persist conversation", err);
    useAppStore.getState().addToast({
      title: "Couldn't save conversation",
      description: "Your changes may be lost if you close the app.",
      variant: "error",
    });
  });
}

interface ConversationsState {
  /** Sidebar/search-friendly list, newest first. */
  summaries: ConversationSummary[];
  /** Full conversations loaded so far, keyed by id (lazy — not every summary
   * has a loaded entry until it's opened). */
  byId: Record<string, Conversation>;
  activeConversationId: string | null;
  loaded: boolean;
  folders: ConversationFolder[];

  init: () => Promise<void>;
  loadFolders: () => Promise<void>;
  createFolder: (name: string) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  setConversationFolder: (id: string, folderId: string | null) => void;
  togglePin: (id: string) => void;
  toggleMessageFavorite: (conversationId: string, messageId: string) => void;
  duplicateConversation: (id: string) => Promise<Conversation | null>;
  branchConversation: (id: string, atMessageId: string) => Promise<Conversation | null>;
  loadConversation: (id: string) => Promise<Conversation | null>;
  selectConversation: (id: string | null) => void;
  createConversation: (modelId?: string | null) => Conversation;
  renameConversation: (id: string, title: string) => void;
  deleteConversation: (id: string) => void;
  clearConversation: (id: string) => void;
  setConversationModel: (id: string, modelId: string) => void;
  setConversationParams: (id: string, patch: Partial<ChatParams>) => void;
  setConversationKnowledgeScope: (id: string, scope: KnowledgeScope) => void;

  appendMessage: (conversationId: string, message: ChatMessage) => void;
  /** In-memory only — the streaming hot path. Callers persist via
   * `setMessageStatus` once the stream ends. */
  updateMessageContent: (conversationId: string, messageId: string, content: string) => void;
  setMessageStatus: (
    conversationId: string,
    messageId: string,
    status: MessageStatus,
    error?: string,
  ) => void;
  editMessage: (conversationId: string, messageId: string, content: string) => void;
  removeMessage: (conversationId: string, messageId: string) => void;
  /** Drops every message after (not including) `messageId` — used when
   * editing a user message to discard the stale reply that followed it. */
  removeMessagesAfter: (conversationId: string, messageId: string) => void;
}

export const useConversationsStore = create<ConversationsState>()((set, get) => ({
  summaries: [],
  byId: {},
  activeConversationId: null,
  loaded: false,
  folders: [],

  init: async () => {
    if (get().loaded) return;
    try {
      const summaries = await repo.list();
      set({ summaries, loaded: true });
    } catch (err) {
      log.error("Failed to load conversation list", err);
      set({ loaded: true });
    }
    void get().loadFolders();
  },

  loadFolders: async () => {
    if (!isTauri()) return;
    try {
      const folders = await conversationOrganizationRepository.listFolders(
        useSettingsStore.getState().activeWorkspaceId,
      );
      set({ folders });
    } catch (err) {
      log.error("Failed to load conversation folders", err);
    }
  },

  createFolder: async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await conversationOrganizationRepository.createFolder(useSettingsStore.getState().activeWorkspaceId, trimmed);
      await get().loadFolders();
    } catch (err) {
      log.error("Failed to create folder", err);
    }
  },

  deleteFolder: async (id) => {
    try {
      await conversationOrganizationRepository.deleteFolder(id);
      await get().loadFolders();
    } catch (err) {
      log.error("Failed to delete folder", err);
    }
  },

  setConversationFolder: (id, folderId) => {
    set((s) => {
      const conversation = s.byId[id];
      if (!conversation) return s;
      const updated: Conversation = { ...conversation, folderId, updatedAt: Date.now() };
      persistConversation(updated);
      return {
        byId: { ...s.byId, [id]: updated },
        summaries: upsertSummary(s.summaries, toConversationSummary(updated)),
      };
    });
  },

  togglePin: (id) => {
    void (async () => {
      const conversation = await get().loadConversation(id);
      if (!conversation) return;
      const updated: Conversation = { ...conversation, pinned: !conversation.pinned };
      persistConversation(updated);
      set((s) => ({
        byId: { ...s.byId, [id]: updated },
        summaries: upsertSummary(s.summaries, toConversationSummary(updated)),
      }));
    })();
  },

  toggleMessageFavorite: (conversationId, messageId) => {
    set((s) => {
      const conversation = s.byId[conversationId];
      if (!conversation) return s;
      const messages = conversation.messages.map((m) =>
        m.id === messageId ? { ...m, favorite: !m.favorite } : m,
      );
      const updated: Conversation = { ...conversation, messages };
      persistConversation(updated);
      return { byId: { ...s.byId, [conversationId]: updated } };
    });
  },

  duplicateConversation: async (id) => {
    const source = await get().loadConversation(id);
    if (!source) return null;
    const now = Date.now();
    const duplicate: Conversation = {
      ...source,
      id: createId("conv"),
      title: `Copy of ${source.title}`,
      createdAt: now,
      updatedAt: now,
      pinned: false,
      parentConversationId: null,
      branchPointSeq: null,
      messages: source.messages.map((m) => ({ ...m, id: createId("msg") })),
    };
    set((s) => ({
      byId: { ...s.byId, [duplicate.id]: duplicate },
      summaries: upsertSummary(s.summaries, toConversationSummary(duplicate)),
    }));
    persistConversation(duplicate);
    return duplicate;
  },

  branchConversation: async (id, atMessageId) => {
    const source = await get().loadConversation(id);
    if (!source) return null;
    const index = source.messages.findIndex((m) => m.id === atMessageId);
    if (index === -1) return null;
    const now = Date.now();
    const branch: Conversation = {
      ...source,
      id: createId("conv"),
      title: `${source.title} (branch)`,
      createdAt: now,
      updatedAt: now,
      pinned: false,
      parentConversationId: source.id,
      branchPointSeq: index,
      messages: source.messages.slice(0, index + 1).map((m) => ({ ...m, id: createId("msg") })),
    };
    set((s) => ({
      byId: { ...s.byId, [branch.id]: branch },
      summaries: upsertSummary(s.summaries, toConversationSummary(branch)),
    }));
    persistConversation(branch);
    return branch;
  },

  loadConversation: async (id) => {
    const existing = get().byId[id];
    if (existing) return existing;
    try {
      const raw = await repo.get(id);
      const conversation = raw ? normalize(raw) : null;
      if (conversation) {
        set((s) => ({ byId: { ...s.byId, [id]: conversation } }));
      }
      return conversation;
    } catch (err) {
      log.error("Failed to load conversation", err);
      return null;
    }
  },

  selectConversation: (id) => {
    set({ activeConversationId: id });
    if (id) void get().loadConversation(id);
  },

  createConversation: (modelId) => {
    const settings = useSettingsStore.getState();
    const now = Date.now();
    const conversation: Conversation = {
      id: createId("conv"),
      title: "New chat",
      createdAt: now,
      updatedAt: now,
      modelId: modelId ?? settings.defaultModelId,
      params: { ...settings.defaultChatParams },
      messages: [],
      titleGenerated: false,
      knowledgeScope: { ...DEFAULT_KNOWLEDGE_SCOPE },
      workspaceId: settings.activeWorkspaceId,
      pinned: false,
      folderId: null,
      parentConversationId: null,
      branchPointSeq: null,
    };
    set((s) => ({
      byId: { ...s.byId, [conversation.id]: conversation },
      summaries: upsertSummary(s.summaries, toConversationSummary(conversation)),
      activeConversationId: conversation.id,
    }));
    persistConversation(conversation);
    return conversation;
  },

  renameConversation: (id, title) => {
    set((s) => {
      const conversation = s.byId[id];
      if (!conversation) return s;
      // A rename (manual or auto-generated) locks the title so future
      // auto-title generation never overwrites it.
      const updated: Conversation = { ...conversation, title, titleGenerated: true, updatedAt: Date.now() };
      persistConversation(updated);
      return {
        byId: { ...s.byId, [id]: updated },
        summaries: upsertSummary(s.summaries, toConversationSummary(updated)),
      };
    });
  },

  deleteConversation: (id) => {
    repo.delete(id).catch((err: unknown) => log.error("Failed to delete conversation", err));
    set((s) => {
      const byId = { ...s.byId };
      delete byId[id];
      return {
        byId,
        summaries: s.summaries.filter((sum) => sum.id !== id),
        activeConversationId: s.activeConversationId === id ? null : s.activeConversationId,
      };
    });
  },

  clearConversation: (id) => {
    set((s) => {
      const conversation = s.byId[id];
      if (!conversation) return s;
      const updated: Conversation = { ...conversation, messages: [], updatedAt: Date.now() };
      persistConversation(updated);
      return {
        byId: { ...s.byId, [id]: updated },
        summaries: upsertSummary(s.summaries, toConversationSummary(updated)),
      };
    });
  },

  setConversationModel: (id, modelId) => {
    set((s) => {
      const conversation = s.byId[id];
      if (!conversation) return s;
      const updated: Conversation = { ...conversation, modelId, updatedAt: Date.now() };
      persistConversation(updated);
      return {
        byId: { ...s.byId, [id]: updated },
        summaries: upsertSummary(s.summaries, toConversationSummary(updated)),
      };
    });
  },

  setConversationParams: (id, patch) => {
    set((s) => {
      const conversation = s.byId[id];
      if (!conversation) return s;
      const updated: Conversation = {
        ...conversation,
        params: { ...conversation.params, ...patch },
        updatedAt: Date.now(),
      };
      persistConversation(updated);
      return { byId: { ...s.byId, [id]: updated } };
    });
  },

  setConversationKnowledgeScope: (id, scope) => {
    set((s) => {
      const conversation = s.byId[id];
      if (!conversation) return s;
      const updated: Conversation = { ...conversation, knowledgeScope: scope, updatedAt: Date.now() };
      persistConversation(updated);
      return { byId: { ...s.byId, [id]: updated } };
    });
  },

  appendMessage: (conversationId, message) => {
    set((s) => {
      const conversation = s.byId[conversationId];
      if (!conversation) return s;
      const updated: Conversation = {
        ...conversation,
        messages: [...conversation.messages, message],
        updatedAt: Date.now(),
      };
      persistConversation(updated);
      return {
        byId: { ...s.byId, [conversationId]: updated },
        summaries: upsertSummary(s.summaries, toConversationSummary(updated)),
      };
    });
  },

  updateMessageContent: (conversationId, messageId, content) => {
    set((s) => {
      const conversation = s.byId[conversationId];
      if (!conversation) return s;
      const messages = conversation.messages.map((m) => (m.id === messageId ? { ...m, content } : m));
      return { byId: { ...s.byId, [conversationId]: { ...conversation, messages } } };
    });
  },

  setMessageStatus: (conversationId, messageId, status, error) => {
    set((s) => {
      const conversation = s.byId[conversationId];
      if (!conversation) return s;
      const messages = conversation.messages.map((m) =>
        m.id === messageId ? { ...m, status, error } : m,
      );
      const updated: Conversation = { ...conversation, messages, updatedAt: Date.now() };
      persistConversation(updated);
      return {
        byId: { ...s.byId, [conversationId]: updated },
        summaries: upsertSummary(s.summaries, toConversationSummary(updated)),
      };
    });
  },

  editMessage: (conversationId, messageId, content) => {
    set((s) => {
      const conversation = s.byId[conversationId];
      if (!conversation) return s;
      const messages = conversation.messages.map((m) => (m.id === messageId ? { ...m, content } : m));
      const updated: Conversation = { ...conversation, messages, updatedAt: Date.now() };
      persistConversation(updated);
      return { byId: { ...s.byId, [conversationId]: updated } };
    });
  },

  removeMessage: (conversationId, messageId) => {
    set((s) => {
      const conversation = s.byId[conversationId];
      if (!conversation) return s;
      const messages = conversation.messages.filter((m) => m.id !== messageId);
      const updated: Conversation = { ...conversation, messages, updatedAt: Date.now() };
      persistConversation(updated);
      return {
        byId: { ...s.byId, [conversationId]: updated },
        summaries: upsertSummary(s.summaries, toConversationSummary(updated)),
      };
    });
  },

  removeMessagesAfter: (conversationId, messageId) => {
    set((s) => {
      const conversation = s.byId[conversationId];
      if (!conversation) return s;
      const index = conversation.messages.findIndex((m) => m.id === messageId);
      if (index === -1) return s;
      const messages = conversation.messages.slice(0, index + 1);
      const updated: Conversation = { ...conversation, messages, updatedAt: Date.now() };
      persistConversation(updated);
      return {
        byId: { ...s.byId, [conversationId]: updated },
        summaries: upsertSummary(s.summaries, toConversationSummary(updated)),
      };
    });
  },
}));
